import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import {
    CreateInvoiceUpdatePayload,
    GetInvoicesRow,
    InvoiceDetail,
    InvoiceItem,
    InvoicePayment,
    InvoiceTransaction,
    InvoiceUpdate,
    MutationInvoiceItemParams,
    TransactionParams,
    InvoicePrefillEmailData,
    InvoiceSendMailPayload,
    InvoiceMailTask,
    PreviewVatItem,
    EInvoice,
    EInvoiceItemDescription,
    EInvoiceItems,
    EInvoicePreview,
    EInvoicePublishPayload,
    SendSignedDocumentMailPayload,
    SignedDocument,
    SignedDocumentImage,
    SignedDocumentImageSource,
    SignedDocumentMailPrefill,
    SignedDocumentType,
} from '../types'
import { SIGNED_DOCUMENTS } from '../constants/signed-documents'

export const getInvoicesStatistical = async (
    payload: Omit<DataGridPayload, 'page' | 'page_size'>
): Promise<
    ResponseArray<{
        count: number
        invoice_status: string
        total_amount: number
        revenue_confirmed: boolean
        invoice_type: string
    }>
> => {
    const response = await datacentral.get(
        `/invoices/statistical?${qs.stringify(payload)}`
    )
    return response?.data
}

export const getInvoices = async (
    payload: DataGridPayload
): Promise<ResponseArray<GetInvoicesRow>> => {
    const response = await datacentral.get(`/invoices?${qs.stringify(payload)}`)
    return response?.data
}

export const getInvoiceByInvoiceId = async (
    invoiceId: number
): Promise<ResponseObject<InvoiceDetail>> => {
    const response = await datacentral.get<ResponseObject<InvoiceDetail>>(
        `/invoices/${invoiceId}`
    )
    return response?.data
}

export const approvedInvoice = async ({
    invoiceId,
    approvedMethod = 'non-invoicable',
}: {
    invoiceId: number
    approvedMethod?: string
}) => {
    const response = await datacentral.post(`/invoices/${invoiceId}/approved`, {
        status: approvedMethod,
    })
    return response?.data
}

/**
 *
 * @param invoiceId - Invoice ID
 * @param category - Cancel category
 * @param message - Cancel message
 * @returns ResponseObject<InvoiceDetail>
 */
export const cancalledInvoice = async (
    invoiceId: number,
    category: string,
    message: string
) => {
    const response = await datacentral.post(
        `/invoices/${invoiceId}/cancelled`,
        {
            category,
            message,
        }
    )
    return response?.data
}

/**
 *
 * @param invoiceId - Invoice ID
 * @param category - Delete category
 * @param message - Delete message
 * @returns ResponseObject<InvoiceDetail>
 */
export const deleteInvoice = async (
    invoiceId: number,
    category: string,
    message: string
) => {
    const response = await datacentral.delete(`/invoices/${invoiceId}`, {
        params: {
            category,
            message,
        },
    })
    return response?.data
}

export const getInvoiceLogs = async (
    payload: DataGridPayload & { invoiceId: number }
) => {
    const response = await datacentral.get(
        `/invoices/${payload.invoiceId}/logs?${qs.stringify(payload)}`
    )
    return response?.data
}

export const deleteInvoiceItem = async (params: MutationInvoiceItemParams) => {
    const response = await datacentral.delete(
        `/invoices/${params.invoice_id}/items/${params.invoice_item_id}`
    )
    return response?.data
}

export const getInvoiceItems = async (
    invoiceId: number
): Promise<ResponseArray<InvoiceItem>> => {
    const response = await datacentral.get(`/invoices/${invoiceId}/items`)
    return response?.data
}

export const createInvoiceTransaction = async ({
    invoiceID,
    data,
}: {
    invoiceID: number
    data: TransactionParams & {
        credit_id: number
        invoice_payment_at: string
    }
}) => {
    const response = await datacentral.post(
        `/invoices/${invoiceID}/transactions`,
        {
            ...data,
        }
    )
    return response?.data
}
export const getInvoiceTransactions = async ({
    invoiceID,
}: {
    invoiceID: number
}) => {
    const response = await datacentral.get<ResponseArray<InvoiceTransaction>>(
        `/invoices/${invoiceID}/transactions`
    )
    return response?.data
}

export const getInvoicePaymentTotal = async ({
    invoiceID,
}: {
    invoiceID: number
}) => {
    const response = await datacentral.get<ResponseObject<InvoicePayment>>(
        `/invoices/${invoiceID}/paymented`
    )
    return response?.data
}
export const createInvoiceUpdate = async ({
    invoice_id,
    data,
}: CreateInvoiceUpdatePayload) => {
    const response = await datacentral.post<ResponseObject<InvoiceUpdate>>(
        `/invoices/${invoice_id}/invoice-updates`,
        data
    )
    return response.data
}

export const createInvoiceItem = async (data: Partial<InvoiceItem>) => {
    const response = await datacentral.post(
        `/invoices/${data.invoice_id}/items`,
        data
    )
    return response?.data
}

export const updateInvoiceItem = async (data: Partial<InvoiceItem>) => {
    const response = await datacentral.put(
        `/invoices/${data.invoice_id}/items/${data.invoice_item_id}`,
        data
    )
    return response?.data
}

export const updateInvoiceExporterCode = async ({
    invoiceID,
    code,
}: {
    invoiceID: number
    code?: string | number
}) => {
    const response = await datacentral.put(
        `/invoices/${invoiceID}/exporter_code`,
        { code }
    )
    return response?.data
}

export const getInvoicePreview = async ({
    invoiceID,
    type,
}: {
    invoiceID: number
    type: 'renew' | 'statement'
}) => {
    const url =
        type === 'renew' ? 'preview.extend.html' : 'preview.statement.html'
    const response = await datacentral.get(`/invoices/${invoiceID}/${url}`)
    return response?.data
}

// ─── Chứng từ ký số (giấy đề nghị thanh toán, biên bản đối soát) ─────────────────
// 2 loại cùng bộ API, khác base path + tên field id (constants/signed-documents.ts).

const documentURL = (type: SignedDocumentType, invoiceID: number) =>
    `/invoices/${invoiceID}/${SIGNED_DOCUMENTS[type].path}`

type SignedDocumentResponse = Omit<
    SignedDocument,
    'document_id' | 'history'
> & {
    history?: SignedDocumentResponse[]
}

/** Gán `document_id` chung từ payment_request_id / reconciliation_statement_id (cả history) */
const normalizeDocument = (
    type: SignedDocumentType,
    document: SignedDocumentResponse
): SignedDocument => ({
    ...document,
    document_id: document[SIGNED_DOCUMENTS[type].idField] ?? 0,
    history: document.history?.map((item) => normalizeDocument(type, item)),
})

const normalizeResponse = (
    type: SignedDocumentType,
    response: ResponseObject<SignedDocumentResponse>
): ResponseObject<SignedDocument> => ({
    ...response,
    result: response?.result && normalizeDocument(type, response.result),
})

// image_source chỉ gửi cho loại có trang Hình ảnh (biên bản); giấy đề nghị bỏ qua
const imageSourceParam = (
    type: SignedDocumentType,
    imageSource?: SignedDocumentImageSource
) =>
    SIGNED_DOCUMENTS[type].hasImages && imageSource
        ? { image_source: imageSource }
        : {}

export const getSignedDocumentPreview = async (
    type: SignedDocumentType,
    invoiceID: number,
    imageSource?: SignedDocumentImageSource
): Promise<string> => {
    const response = await datacentral.get(
        `${documentURL(type, invoiceID)}/preview.html?${qs.stringify(imageSourceParam(type, imageSource))}`
    )
    return response?.data
}

export const signSignedDocument = async (
    type: SignedDocumentType,
    invoiceID: number,
    imageSource?: SignedDocumentImageSource
): Promise<ResponseObject<SignedDocument>> => {
    const response = await datacentral.post(
        `${documentURL(type, invoiceID)}/sign`,
        imageSourceParam(type, imageSource)
    )
    return normalizeResponse(type, response?.data)
}

// Huỷ bản đã ký và ký bản mới theo dữ liệu hoá đơn hiện tại, trong 1 lần gọi
export const resignSignedDocument = async ({
    type,
    invoiceID,
    reason,
    imageSource,
}: {
    type: SignedDocumentType
    invoiceID: number
    reason: string
    imageSource?: SignedDocumentImageSource
}): Promise<ResponseObject<SignedDocument>> => {
    const response = await datacentral.post(
        `${documentURL(type, invoiceID)}/resign`,
        { reason, ...imageSourceParam(type, imageSource) }
    )
    return normalizeResponse(type, response?.data)
}

export const getSignedDocument = async (
    type: SignedDocumentType,
    invoiceID: number
): Promise<ResponseObject<SignedDocument>> => {
    const response = await datacentral.get(documentURL(type, invoiceID))
    return normalizeResponse(type, response?.data)
}

// Hình upload cho trang "Hình ảnh" (image_source = upload; chỉ loại hasImages).
// Cả 3 API trả danh sách hiện tại.
export const getSignedDocumentImages = async (
    type: SignedDocumentType,
    invoiceID: number
): Promise<ResponseObject<SignedDocumentImage[]>> => {
    const response = await datacentral.get(
        `${documentURL(type, invoiceID)}/images`
    )
    return response?.data
}

export const uploadSignedDocumentImages = async ({
    type,
    invoiceID,
    files,
}: {
    type: SignedDocumentType
    invoiceID: number
    files: File[]
}): Promise<ResponseObject<SignedDocumentImage[]>> => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    const response = await datacentral.post(
        `${documentURL(type, invoiceID)}/images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response?.data
}

export const deleteSignedDocumentImage = async ({
    type,
    invoiceID,
    imageID,
}: {
    type: SignedDocumentType
    invoiceID: number
    imageID: number
}): Promise<ResponseObject<SignedDocumentImage[]>> => {
    const response = await datacentral.delete(
        `${documentURL(type, invoiceID)}/images/${imageID}`
    )
    return response?.data
}

// Gửi mail chứng từ đã ký cho khách (backend tự đính kèm PDF)
export const getSignedDocumentMailPrefill = async (
    type: SignedDocumentType,
    invoiceID: number
): Promise<ResponseObject<SignedDocumentMailPrefill>> => {
    const response = await datacentral.get(
        `${documentURL(type, invoiceID)}/mail-prefill`
    )
    return response?.data
}

// Response giống GET trạng thái (đã có mails + last_sent_at mới)
export const sendSignedDocumentMail = async ({
    type,
    invoiceID,
    ...payload
}: SendSignedDocumentMailPayload & {
    type: SignedDocumentType
    invoiceID: number
}): Promise<ResponseObject<SignedDocument>> => {
    const response = await datacentral.post(
        `${documentURL(type, invoiceID)}/send-mail`,
        payload
    )
    return normalizeResponse(type, response?.data)
}

// ─── Hoá đơn điện tử (MISA meInvoice) ─────────────────────────────────────────

const eInvoiceURL = (invoiceID: number) => `/invoices/${invoiceID}/e-invoice`

/** Trả lỗi `e-invoice-not-issued` khi chưa phát hành (không phải lỗi thật) */
export const getEInvoice = async (
    invoiceID: number
): Promise<ResponseObject<EInvoice>> => {
    const response = await datacentral.get(eInvoiceURL(invoiceID))
    return response?.data
}

export const getEInvoiceItems = async (
    invoiceID: number
): Promise<ResponseObject<EInvoiceItems>> => {
    const response = await datacentral.get(`${eInvoiceURL(invoiceID)}/items`)
    return response?.data
}

/** Lưu nội dung dòng hàng kế toán sửa (chưa phát hành); lần xem trước / phát hành sau dùng nội dung này */
export const saveEInvoiceItems = async ({
    invoiceID,
    item_descriptions,
}: {
    invoiceID: number
    item_descriptions: EInvoiceItemDescription[]
}): Promise<ResponseObject<EInvoiceItems>> => {
    const response = await datacentral.put(`${eInvoiceURL(invoiceID)}/items`, {
        item_descriptions,
    })
    return response?.data
}

export const previewEInvoice = async ({
    invoiceID,
    ...payload
}: EInvoicePublishPayload & {
    invoiceID: number
}): Promise<ResponseObject<EInvoicePreview>> => {
    const response = await datacentral.post(
        `${eInvoiceURL(invoiceID)}/preview`,
        payload
    )
    return response?.data
}

// MISA / phát hành song song có thể chậm: timeout ≥ 120 giây (axios mặc định không giới hạn)
export const publishEInvoice = async ({
    invoiceID,
    ...payload
}: EInvoicePublishPayload & {
    invoiceID: number
}): Promise<ResponseObject<EInvoice>> => {
    const response = await datacentral.post(
        `${eInvoiceURL(invoiceID)}/publish`,
        payload,
        { timeout: 150_000 }
    )
    return response?.data
}

export const markEInvoiceWrong = async ({
    invoiceID,
    reason,
}: {
    invoiceID: number
    reason: string
}): Promise<ResponseObject<EInvoice>> => {
    const response = await datacentral.post(
        `${eInvoiceURL(invoiceID)}/mark-wrong`,
        { reason }
    )
    return response?.data
}

export const getInvoicePrefillEmail = async (
    invoiceID: number
): Promise<ResponseObject<InvoicePrefillEmailData>> => {
    const response = await datacentral.get(
        `/invoices/${invoiceID}/prefill-email`
    )
    return response?.data
}

export const sendInvoiceMail = async ({
    invoiceID,
    data,
}: {
    invoiceID: number
    data: InvoiceSendMailPayload
}): Promise<ResponseObject<any>> => {
    const response = await datacentral.post(
        `/invoices/${invoiceID}/send-mail`,
        data
    )
    return response?.data
}

export const getInvoiceMailTasks = async (
    payload: DataGridPayload & { invoiceId: number }
): Promise<ResponseArray<InvoiceMailTask>> => {
    const { invoiceId, ...restPayload } = payload
    const response = await datacentral.get(
        `/mailers/tasks/invoices/${invoiceId}?${qs.stringify(restPayload)}`
    )
    return response?.data
}

export const previewVatItems = async (params: {
    gte: string
    lte: string
    price: number
    product_group_name: string
    service_id?: number
}): Promise<ResponseArray<PreviewVatItem>> => {
    const response = await datacentral.get(
        `/invoices/preview-vat-items?${qs.stringify(params)}`
    )
    return response?.data
}

export const rollbackInvoice = async ({
    invoiceID,
    message,
}: {
    invoiceID: number
    message: string
}) => {
    const response = await datacentral.post(`/invoices/${invoiceID}/rollback`, {
        message,
    })
    return response?.data
}
